import { round, toFixed, toPercent, toEuro } from '../common'

export default class Simulation {
  dom = {}
  grille = []
  var = {}
  value = {}
  cache = {}

  constructor(options) {
    this.dom = { ...options.dom }
    this.grille = [...options.grille]

    // sanitize js values
    this.tauxFraisGestion = options.var.tauxFraisGestion
    this.capFraisGestion = options.var.capFraisGestion
    this.tauxMajoration = options.var.tauxMajoration

    for (const [index, row] of this.grille.entries()) {
      const { min, max, pat, sal } = row
      this.grille[index].min = min === '' ? 0 : parseInt(min, 10)
      this.grille[index].max = max === '' ? 9999 : parseInt(max, 10)
      this.grille[index].pat = pat === '' ? 0 : round(parseFloat(pat) / 100, 4)
      this.grille[index].sal = pat === '' ? 0 : round(parseFloat(sal) / 100, 4)
    }

    this.dom.form.addEventListener('input', this.input.bind(this))
    this.input()
  }

  set tauxFraisGestion(str) {
    this.var.tauxFraisGestion = round(parseFloat(str) / 100, 4)
    this.value.tauxFraisGestion = toPercent(this.var.tauxFraisGestion, 2)
  }

  set tauxMajoration(str) {
    this.var.tauxMajoration = round(parseFloat(str) / 100, 4)
    this.value.tauxMajoration = toPercent(this.var.tauxMajoration, 2)
  }

  set tauxChargesPatronales(num) {
    this.var.tauxChargesPatronales = round(num, 4)
    this.value.tauxChargesPatronales = toPercent(num, 2)
  }

  set tauxChargesSalariales(num) {
    this.var.tauxChargesSalariales = round(num, 4)
    this.value.tauxChargesSalariales = toPercent(num, 2)
  }

  set tjm(str) {
    if (typeof str === 'string' && !isNaN(str) && !isNaN(parseFloat(str))) {
      this.var.tjm = parseInt(str, 10)
      this.value.tjm = toEuro(Number(this.var.tjm))
    } else {
      this.var.tjm = null
      this.value.tjm = null
    }
    this.charges()
  }

  set jours(str) {
    if (typeof str === 'string' && !isNaN(str) && !isNaN(parseFloat(str))) {
      this.var.jours = parseInt(str, 10)
      this.value.jours = toFixed(Number(this.var.jours))
    } else {
      this.var.jours = null
      this.value.jours = null
    }
    this.charges()
  }

  set frais(str) {
    if (typeof str === 'string' && !isNaN(str) && !isNaN(parseFloat(str))) {
      this.var.frais = parseInt(str, 10)
      this.value.frais =
        this.var.frais === 0 ? '' : toEuro(Number(this.var.frais))
    } else {
      this.var.frais = null
      this.value.frais = null
    }
  }

  set ca(num) {
    this.var.ca = round(num)
    this.value.ca = toEuro(num)
  }

  set valeurFraisGestion(num) {
    if (this.capFraisGestion && num > this.capFraisGestion) {
      num = this.capFraisGestion
    }
    this.var.valeurFraisGestion = round(num)
    this.value.valeurFraisGestion = toEuro(num)
  }

  set fraisPortage(num) {
    if (this.capFraisGestion && num > this.capFraisGestion) {
      num = this.capFraisGestion
    }
    this.var.fraisPortage = round(num)
    this.value.fraisPortage = toEuro(num)
  }

  set salaireBrut(num) {
    this.var.salaireBrut = round(num)
    this.value.salaireBrut = toEuro(num)
  }

  set valeurChargesPat(num) {
    this.var.valeurChargesPat = round(num)
    this.value.valeurChargesPat = toEuro(num)
  }

  set valeurChargesSal(num) {
    this.var.valeurChargesSal = round(num)
    this.value.valeurChargesSal = toEuro(num)
  }

  set valeurCharges(num) {
    this.var.valeurCharges = round(num)
    this.value.valeurCharges = toEuro(num)
  }

  set salaireNet(num) {
    this.var.salaireNet = round(num)
    this.value.salaireNet = toEuro(num)
  }

  set netRestitue(num) {
    this.var.netRestitue = round(num)
    this.value.netRestitue = toEuro(num)
  }

  set percentRestitue(num) {
    this.var.percentRestitue = round(num, 4)
    this.value.percentRestitue = toPercent(num, 2)
  }

  set salaireNetMajore(num) {
    this.var.salaireNetMajore = round(num)
    this.value.salaireNetMajore = toEuro(num)
  }

  set graphSalaireNet(num) {
    this.var.graphSalaireNet = round(num, 3)
    this.value.graphSalaireNet = toPercent(num, 2)
  }

  set graphFrais(num) {
    this.var.graphFrais = round(num, 3)
    this.value.graphFrais = toPercent(num, 2)
  }

  set graphCharges(num) {
    this.var.graphCharges = round(num, 3)
    this.value.graphCharges = toPercent(num, 2)
  }

  set graphFraisGestion(num) {
    this.var.graphFraisGestion = round(num, 3)
    this.value.graphFraisGestion = toPercent(num, 2)
  }

  input() {
    this.tjm = this.dom.tjm.value
    this.jours = this.dom.jours.value
    this.frais = this.dom.frais.value

    this.charges()

    this.valid =
      this.var.tjm !== null &&
      this.var.jours !== null &&
      this.var.tauxChargesPatronales !== null &&
      this.var.tauxChargesSalariales !== null &&
      this.dom.tjm.validity.valid &&
      this.dom.jours.validity.valid &&
      this.dom.frais.validity.valid

    this.dom.process.disabled = !this.valid

    this.calc()
  }

  calc() {
    if (this.valid) {
      this.ca = this.var.tjm * this.var.jours
      this.valeurFraisGestion = this.var.ca * this.var.tauxFraisGestion
      this.fraisPortage = this.var.ca * this.var.tauxFraisGestion
      const salaireBrut =
        (this.var.ca - this.var.frais - this.var.fraisPortage) /
        (1 + this.var.tauxChargesPatronales)
      this.salaireBrut = salaireBrut < 0 ? 0 : salaireBrut
      this.valeurChargesPat =
        this.var.salaireBrut * this.var.tauxChargesPatronales
      this.valeurChargesSal =
        this.var.salaireBrut * this.var.tauxChargesSalariales
      this.valeurCharges = this.var.valeurChargesPat + this.var.valeurChargesSal
      const salaireNet =
        this.var.salaireBrut * (1 - this.var.tauxChargesSalariales)
      this.salaireNet = salaireNet < 0 ? 0 : salaireNet
      this.netRestitue = this.var.salaireNet + this.var.frais
      this.percentRestitue = this.var.netRestitue / this.var.ca
      this.salaireNetMajore =
        this.var.netRestitue + this.var.netRestitue * this.var.tauxMajoration
      this.graphSalaireNet = this.var.salaireNet / this.var.ca
      this.graphFrais = this.var.frais / this.var.ca
      this.graphCharges = this.var.valeurCharges / this.var.ca
      this.graphFraisGestion = this.var.valeurFraisGestion / this.var.ca
    } else {
      this.ca = null
      this.valeurFraisGestion = null
      this.fraisPortage = null
      this.salaireBrut = null
      this.valeurChargesPat = null
      this.valeurChargesSal = null
      this.valeurCharges = null
      this.salaireNet = null
      this.netRestitue = null
      this.percentRestitue = null
      this.salaireNetMajore = null
      this.graphSalaireNet = null
      this.graphFrais = null
      this.graphCharges = null
      this.graphFraisGestion = null
    }
    this.update()
  }

  update() {
    for (const [key, value] of Object.entries(this.value)) {
      for (const el of this.getVarElements(key)) el.innerHTML = value
    }
    this.dom.form.dispatchEvent(new Event('update'))
  }

  getVarElements(key) {
    if (!this.cache[key]) {
      this.cache[key] = document.querySelectorAll(`[data-value="${key}"]`)
    }
    return this.cache[key]
  }

  charges() {
    let tauxChargesPatronales = null
    let tauxChargesSalariales = null
    if (this.var.tjm !== null && this.var.jours !== null) {
      for (const row of this.grille) {
        const { min, max, pat, sal } = row
        if (this.var.tjm >= min && this.var.tjm <= max) {
          tauxChargesPatronales = pat
          tauxChargesSalariales = sal
        }
      }
    }
    this.tauxChargesPatronales = tauxChargesPatronales
    this.tauxChargesSalariales = tauxChargesSalariales
  }
}
